import { createRouter, createWebHistory } from 'vue-router'

import { useMainStore } from '@/store/main'
import { useLocationStore } from '@/store/location'
import { useCoreFeaturesStore } from '@/store/coreFeatures'
import { useStatusStore } from '@/store/status'
import { useSubscriptionStore } from '@/store/subscription'
import { loadLanguageAsync } from '@/i18n'
import ScreenLayout from '@/components/layouts/ScreenLayout'

import { funnelRoutes } from '@/funnels/funnelUtils.ts'
import { DOMAINS_FROM_CONFIG } from '@/funnels/funnels.ts'
import subscriptionRoutes from '@/router/account/subscriptionRoutes'
import paywallRoutes from '@/modules/Paywall/paywallRoutes.ts'
import upsellRoutes from '@/modules/Upsell/upsellRoutes.ts'
import prepayWallRoutes from '@/modules/PrePaywall/prePaywallRoutes.ts'
import manageSubscriptionRoutes from '@/router/account/manageSubscriptionRoutes'
import redirects from '@/router/redirects'

// basePath is resolved lazily in the router's history option
// since the location store needs pinia to be installed first
let _basePath = null
function getBasePath() {
	if (_basePath === null) {
		const locationStore = useLocationStore()
		_basePath = locationStore.basePath
	}
	return _basePath
}

const routes = [
	{
		path: '/',
		component: ScreenLayout,
		children: [
			{
				path: '',
				name: 'landing',
				component: () => import('@/modules/Landing/LandingView.vue'),
				meta: {
					isLanding: true,
					hideHeader: true,
				},
			},
			{
				path: '72lbsdown',
				name: 'landing-taboola',
				component: () => import(/* webpackChunkName: "marketing" */ '@/views/landings/LandingTaboola.vue'),
				meta: {
					hideHeader: true,
				},
			},
			{
				name: 'spin-and-win-p',
				component: () => import(/* webpackChunkName: "spin-wheel-offer" */ '@/views/WheelOfFortune.vue'),
				path: 'spin-and-win-p',
				meta: {
					hideHeader: true,
				},
				props: {
					wheelTheme: 'purple',
				},
			},
			{
				name: 'spin-and-win-dp',
				component: () => import(/* webpackChunkName: "spin-wheel-offer" */ '@/views/WheelOfFortune.vue'),
				path: 'spin-and-win-dp',
				meta: {
					hideHeader: true,
				},
				props: {
					wheelTheme: 'purple-dark',
				},
			},
			{
				name: 'spin-and-win-g',
				component: () => import(/* webpackChunkName: "spin-wheel-offer" */ '@/views/WheelOfFortune.vue'),
				path: 'spin-and-win-g',
				meta: {
					hideHeader: true,
				},
				props: {
					wheelTheme: 'green',
				},
			},
			{
				path: 'account',
				alias: '/survey/account',
				meta: {
					hideHeader: true,
				},
				children: [
					{
						path: '',
						name: 'account',
						component: () => import(/* webpackChunkName: "account" */ '@/views/account/Account.vue'),
						meta: {
							requiresAuth: true,
							requiresSubscription: true,
							requiresUser: true,
						},
					},
					...manageSubscriptionRoutes,
					...subscriptionRoutes,
				],
			},

			{
				path: 'faq',
				name: 'faq',
				alias: '/survey/faq',
				component: () => import(/* webpackChunkName: "srv" */ '@/views/Faq.vue'),
				meta: {
					hideHeader: true,
				},
			},

			{
				path: 'contact-us',
				name: 'contact-us',
				component: () => import(/* webpackChunkName: "srv" */ '@/views/ContactUs.vue'),
				meta: {
					hideHeader: true,
				},
			},

			{
				path: 'result_complete',
				name: 'result_complete',
				component: () => import(/* webpackChunkName: "result" */ '@/views/ResultComplete.vue'),
				meta: {
					hideHeader: true,
					requiresUser: true,
				},
			},

			{
				path: 'result',
				name: 'result',
				component: () => import(/* webpackChunkName: "result" */ '@/views/pre-paywall/Result.vue'),
			},
			{
				path: 'awards',
				name: 'awards',
				component: () => import(/* webpackChunkName: "result" */ '@/views/Awards.vue'),
				meta: {
					hideHeader: true,
				},
			},

			{
				path: 'name-form',
				name: 'name-form',
				component: () => import(/* webpackChunkName: "result" */ '@/views/NameForm.vue'),
				meta: {
					requiresUser: true,
				},
			},

			{
				path: 'login',
				name: 'create_account',
				component: () => import(/* webpackChunkName: "login" */ '@/views/Login.vue'),
				meta: {
					requiresUser: true,
					gtm: 'login',
				},
			},

			{
				path: 'signin',
				name: 'signin',
				component: () => import(/* webpackChunkName: "signin" */ '@/views/SignIn.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					skipExperiments: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'AccountSignInScreen',
						},
					},
				},
				props: {
					mode: 'default',
				},
			},

			{
				path: 'signin-auth0',
				name: 'signin-auth0',
				component: () => import(/* webpackChunkName: "signin" */ '@/views/SignIn.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					skipExperiments: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'AccountSignInScreen',
						},
					},
				},
				props: {
					mode: 'auth0',
				},
			},

			{
				path: 'signin-email-code-handler',
				name: 'signin-email-code-handler',
				component: () => import(/* webpackChunkName: "signin-email-handler" */ '@/views/SignInEmailHandler.vue'),
				meta: {
					hideHeader: true,
					skipExperiments: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'SignEmailCodeHandlerScreen',
						},
					},
				},
			},

			{
				path: 'signin-confirmation-email-handler',
				name: 'signin-confirmation-email-handler',
				component: () => import(/* webpackChunkName: "update-profile" */ '@/views/download/Index'),
				props: {
					templateType: 'signin-confirmation',
				},
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'SignEmailCodeHandlerScreen',
						},
					},
				},
			},

			{
				path: 'login_by_code',
				name: 'login_by_code',
				alias: '/survey/login_by_code',
				component: () => import(/* webpackChunkName: "login_by_code" */ '@/views/LoginByCode.vue'),
				meta: {
					requiresUser: true,
					skipExperiments: true,
				},
			},

			{
				path: 'download',
				name: 'download',
				alias: '/survey/download',
				component: () => import(/* webpackChunkName: "update-profile" */ '@/views/download/Index'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'DownloadAppScreen',
						},
					},
				},
			},

			{
				path: 'cancel_subscription',
				name: 'cancel_subscription',
				component: () => import(/* webpackChunkName: "account" */ '@/views/account/CancelSubscription.vue'),
				meta: {
					requiresAuth: true,
					requiresSubscription: true,
					requiresUser: true,
				},
			},

			{
				path: 'withdrawal',
				name: 'withdrawal',
				component: () => import(/* webpackChunkName: "withdrawal" */ '@/views/withdrawal/WithdrawalOptions.vue'),
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'WITHDRAWAL_FIND_A_SOLUTION_SCREEN',
						},
					},
				},
			},

			{
				path: 'withdrawal-form',
				name: 'withdrawal-form',
				component: () => import(/* webpackChunkName: "withdrawal" */ '@/views/withdrawal/WithdrawalForm.vue'),
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'WITHDRAWAL_FORM_SCREEN',
						},
					},
				},
			},

			{
				path: 'withdrawal-loader',
				name: 'withdrawal-loader',
				component: () => import(/* webpackChunkName: "withdrawal" */ '@/views/withdrawal/WithdrawalLoader.vue'),
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'WITHDRAWAL_LOADING_SCREEN',
						},
					},
				},
			},

			{
				path: 'withdrawal-success',
				name: 'withdrawal-success',
				component: () => import(/* webpackChunkName: "withdrawal" */ '@/views/withdrawal/WithdrawalSuccess.vue'),
				meta: {
					hideHeader: true,
				},
			},

			{
				path: 'confirm_email',
				name: 'confirm_email',
				component: () => import(/* webpackChunkName: "account" */ '@/views/account/ConfirmEmail.vue'),
				meta: {
					hideHeader: true,
					requiresUser: true,
				},
			},

			{
				path: 'change_email',
				name: 'change_email',
				component: () => import(/* webpackChunkName: "account" */ '@/views/account/ChangeEmail.vue'),
				meta: {
					requiresUser: true,
				},
			},

			{
				path: 'subscription-success',
				name: 'subscription-success',
				component: () => import(/* webpackChunkName: "old-upsell" */ '@/views/in-app/SubscriptionSuccess.vue'),
				meta: {
					requiresUser: true,
					requiresSubscription: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'ONBOARDING_SUBSCRIPTION_SUCCESS_SCREEN',
						},
					},
				},
			},

			{
				path: 'update-profile',
				name: 'update-profile',
				component: () => import(/* webpackChunkName: "update-profile" */ '@/views/UpdateProfile.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'UpdateProfileScreen',
						},
					},
				},
			},

			{
				path: 'redirect',
				name: 'redirect',
				component: () => import(/* webpackChunkName: "redirect" */ '@/views/Redirect.vue'),
				meta: {
					requiresUser: true,
					skipExperiments: true,
				},
			},

			{
				path: 'personalized-plan-activity',
				name: 'personalized-plan-activity',
				component: () =>
					import(/* webpackChunkName: "pre-paywall" */ '@/views/pre-paywall/PersonalizedPlanActivity.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'ONBOARDING_PLAN_INFO_SCREEN',
						},
					},
				},
			},

			{
				path: 'free-guides',
				name: 'free-guides',
				component: () => import(/* webpackChunkName: "pre-paywall" */ '@/views/pre-paywall/FreeGuides.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'ONBOARDING_GET_FREE_GUIDES_SCREEN',
						},
					},
				},
			},

			{
				path: 'slim-face',
				name: 'slim-face',
				component: () => import('@/views/pre-paywall/SlimFace.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'ONBOARDING_SEE_PREVIEW_SCREEN',
						},
					},
				},
			},
			{
				path: 'slim-face-result',
				name: 'slim-face-result',
				component: () => import('@/views/pre-paywall/SlimFaceResult.vue'),
				meta: {
					requiresUser: true,
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'ONBOARDING_SEE_PREVIEW_RESULT_SCREEN',
						},
					},
				},
			},

			{
				path: 'progress-teaser',
				name: 'progress-teaser',
				component: () => import(/* webpackChunkName: "progress-teaser" */ '@/views/pre-paywall/ProgressTeaser.vue'),
				meta: {
					hideHeader: true,
					pageAnalyticUxEvent: {
						name: 'ScreenView',
						data: {
							title: 'ONBOARDING_PROGRESS_TEASER_SCREEN',
						},
					},
				},
			},
			{
				path: 'your-future-self',
				name: 'your-future-self',
				component: () => import(/* webpackChunkName: "progress-teaser" */ '@/views/YourFutureSelf.vue'),
				meta: {
					hideHeader: true,
					requiresUser: true,
				},
			},
			{
				path: 'motivation-block',
				name: 'motivation-block',
				component: () => import(/* webpackChunkName: "motivation-block" */ '@/views/MotivationBlock.vue'),
				meta: {
					hideHeader: true,
					requiresUser: true,
				},
			},
		],
	},

	...paywallRoutes,
	...upsellRoutes,
	...prepayWallRoutes,
	{
		path: '/assessment',
		component: () => import(/* webpackChunkName: "assessment" */ '@/components/layouts/AssessmentFlowLayout'),
		children: [
			{
				path: 'landing',
				name: 'assessment-landing',
				component: () => import(/* webpackChunkName: "assessment" */ '@/views/assessment/AssessmentLanding.vue'),
			},
			{
				path: 'plan',
				name: 'assessment-plan',
				component: () => import(/* webpackChunkName: "assessment" */ '@/views/assessment/AssessmentPlan.vue'),
			},
			{
				path: 'l-result',
				name: 'assessment-result-landing',
				component: () => import(/* webpackChunkName: "assessment" */ '@/views/assessment/AssessmentResultLanding.vue'),
			},
		],
	},
	{
		path: '/workbooks',
		component: () => import(/* webpackChunkName: "workbook-upsell" */ '@/components/layouts/WorkbooksUpsellLayout'),
		children: [
			{
				path: 'upsell',
				name: 'workbook-upsell',
				component: () => import(/* webpackChunkName: "workbook-upsell" */ '@/views/workbooks/WorkbookUpsell.vue'),
				meta: {
					requiresUser: true,
				},
			},
		],
	},

	{
		path: '/disclaimer',
		name: 'disclaimer',
		component: () => import(/* webpackChunkName: "disclaimer" */ '@/views/HealthNotice.vue'),
	},

	{
		path: '/assessment/report',
		name: 'assessment-report',
		component: () => import(/* webpackChunkName: "assessment" */ '@/views/assessment/AssessmentReport.vue'),
		meta: {
			requiresUser: true,
		},
	},

	{
		path: '/assessment/result',
		name: 'assessment-result',

		component: () => import(/* webpackChunkName: "assessment" */ '@/views/assessment/AssessmentResult.vue'),
	},

	{
		path: '/assessment/f/:page(.*)?',
		name: 'assessmen-survey',
		redirect: { name: 'female-assessment-survey' },
	},

	{
		path: '/assessment/all/:page(.*)?',
		name: 'assessmen-survey-inapp',
		component: () => import(/* webpackChunkName: "assessment-survey" */ '@/views/assessment/AssessmentSurveyInapp.vue'),
		props: { instance: 'femaleAssessment' },
	},

	{
		path: '/assessment/f/:page(.*)?',
		name: 'female-assessment-survey',
		component: () => import(/* webpackChunkName: "assessment-survey" */ '@/views/assessment/AssessmentSurvey.vue'),
		props: { instance: 'femaleAssessment' },
	},
	{
		path: '/find-item-game',
		name: 'find-item-game',
		component: () => import(/* webpackChunkName: "find-item-game" */ '@/views/FindItemGame.vue'),
	},
	{
		path: '/find-character-game',
		name: 'find-character-game',
		component: () => import(/* webpackChunkName: "find-character-game" */ '@/views/FindCharacterGame.vue'),
	},

	...redirects,

	{
		path: '/:pathMatch(.*)*',
		name: '404',
		component: () => import(/* webpackChunkName: "not-found" */ '@/views/EmptyPage.vue'),
	},
]

let _router = null
function getRouter() {
	if (!_router) {
		const hostname = useLocationStore().hostname
		const currentDomainConfig = DOMAINS_FROM_CONFIG.find(({ regex }) => regex.test(hostname))?.domainConfig

		_router = createRouter({
			history: createWebHistory(getBasePath()),
			routes: [...routes, ...funnelRoutes(currentDomainConfig?.pathnames)],
			scrollBehavior(to, from) {
				// Открытие/закрытие модалки не должно скроллить страницу
				if (to.path === from.path && (Boolean(to.query.popup) || Boolean(from.query.popup))) {
					return null
				}

				return new Promise((resolve) => {
					setTimeout(() => {
						resolve({ top: 0, left: 0 })
					}, 0)
				})
			},
		})

		_router.beforeEach(async (to) => {
			const mainStore = useMainStore()
			const coreFeaturesStore = useCoreFeaturesStore()
			const statusStore = useStatusStore()
			const subsStore = useSubscriptionStore()

			const blockingRequests = [
				coreFeaturesStore.hydrateCoreFeatures(),
				mainStore.fetchGeo({
					country: to.query.country,
					region: to.query.region,
				}),
				loadLanguageAsync(),
			]

			// При логине через auth0 происходят редиректы туда-сюда, и юзера мы хотим получать только после того как отработала логика на странице логина
			// иначе появляется состояние гонки(юзер запрашивается со старым токеном вот здесь, а нам нужно сначала выполнить логику со страницы с установкой новых токенов)
			if ((to.name !== 'signin-auth0' && !mainStore.user) || to.meta.requiresExpiredSubscription) {
				const fetchUserRequest = mainStore.fetchUserFromAws({ includeExpired: to.meta.requiresExpiredSubscription })

				if (to.meta.requiresUser) {
					blockingRequests.push(fetchUserRequest)
				}
			}

			await Promise.all(blockingRequests)
			statusStore.setResourceReadiness('network')
			statusStore.setAbsmartlyRequired(!to.meta.skipExperiments)

			let isAuthenticated = mainStore.isAuthenticated
			let isObPreview = mainStore.isPreviewMode

			if (isObPreview) {
				if (!mainStore.user) {
					await mainStore.setupPreviewUser()
				}
				return true
			}
			if (to.meta.requiresAuth && !isAuthenticated) {
				return {
					name: 'create_account',
					query: {
						redirect: to.name,
					},
				}
			}

			if (to.meta.requiresSubscription && !subsStore.hasSubscription && isAuthenticated) {
				return {
					name: 'plan-main',
					query: {
						plan: 'redirect', // FIXME: temporary solution for debug purposes
					},
					replace: true,
				}
			}

			return true
		})
	}
	return _router
}

export default getRouter
